import type { CheckpointPayload } from "./runtime";
import {
  REGION_IDS,
  RULES_VERSION,
  type ReplayEntry,
  type ReplayJournal,
} from "./types";

export const SAVE_FORMAT_VERSION = 1 as const;
export const DEFAULT_SAVE_SIZE_LIMIT = 5 * 1024 * 1024;

export type SaveKind = "autosave" | "manual";

export interface CampaignSaveMetadata {
  campaignId: string;
  label: string;
  kind: SaveKind;
  engineVersion: string;
  rulesVersion: string;
  contentId: string;
  contentDigest: string;
  replayLineageId?: string;
}

export interface CampaignSaveEnvelope extends CampaignSaveMetadata {
  formatVersion: typeof SAVE_FORMAT_VERSION;
  slotId: string;
  savedAt: string;
  revision: number;
  tick: number;
  lastAppliedSequence: number;
  serializedCampaign: string;
  checksum: string;
}

export interface SaveSummary {
  slotId: string;
  campaignId: string;
  label: string;
  kind: SaveKind;
  savedAt: string;
  revision: number;
  tick: number;
}

export interface SaveWriteGuard {
  /** Fencing token obtained from CampaignOwnershipLease. */
  fencingToken?: number;
  /** Reject rather than replacing a save at a newer revision. */
  rejectOlderRevision?: boolean;
}

interface StoredSave extends CampaignSaveEnvelope {
  id: string;
  fencingToken: number;
}

const DB_NAME = "the-control-problem-campaigns";
const DB_VERSION = 1;
const STORE_NAME = "campaign-slots";
const LOCAL_SAVE_PREFIX = "tcp:save:";
const LEASE_PREFIX = "tcp:lease:";
const fallbackMemory = new Map<string, StoredSave>();
const memoryLeases = new Map<string, LeaseRecord>();
const fallbackOwnershipQueues = new Map<string, Promise<unknown>>();
const replayMemory = new Map<string, ReplayJournal>();
const REPLAY_PAGE_SIZE = 128;
export const MAX_REPLAY_EXPORT_ENTRIES = 20_000;
const REPLAY_FALLBACK_ENTRIES = 512;
const REPLAY_PREFIX = "tcp:replay:";
interface ReplayPage { id: string; campaignId: string; recordType: "replay-page"; entries: ReplayEntry[]; }
interface ReplayArchiveIdentity { id: string; campaignId: string; recordType: "replay-identity"; version: ReplayJournal["version"]; rulesVersion: ReplayJournal["rulesVersion"]; numericVersion: ReplayJournal["numericVersion"]; newsVersion?: ReplayJournal["newsVersion"]; seed: number; startRegion: ReplayJournal["startRegion"]; totalAccepted: number; nonReproducible?: boolean; }
const replayId = (lineage: string, page: number | "identity") => `\u0000replay:${lineage}:${page}`;
const entryIdentity = (entry: ReplayEntry) => JSON.stringify({ sequence: entry.sequence, tick: entry.tick, baseRevision: entry.baseRevision, command: Object.fromEntries(Object.entries(entry.command).sort(([a], [b]) => a.localeCompare(b))) });
function validateReplayWindow(journal: ReplayJournal): void {
  if (
    !journal ||
    journal.version !== "TCP-REPLAY-1" ||
    journal.rulesVersion !== RULES_VERSION ||
    journal.numericVersion !== "R01-EXACT-1" ||
    (journal.newsVersion !== undefined && journal.newsVersion !== "N01") ||
    !Number.isSafeInteger(journal.seed) ||
    !REGION_IDS.includes(journal.startRegion) ||
    !Number.isSafeInteger(journal.totalAccepted) ||
    journal.totalAccepted < 0 ||
    !Number.isSafeInteger(journal.firstRetainedSequence) ||
    journal.firstRetainedSequence < 1 ||
    !Array.isArray(journal.entries) ||
    journal.entries.length > 512
  )
    throw new Error("Invalid replay archive window");
  let sequence = journal.firstRetainedSequence - 1,
    tick = -1,
    revision = -1;
  for (const entry of journal.entries) {
    if (
      !Number.isSafeInteger(entry.sequence) ||
      entry.sequence !== sequence + 1 ||
      !Number.isSafeInteger(entry.tick) ||
      entry.tick < 0 ||
      entry.tick < tick ||
      !Number.isSafeInteger(entry.baseRevision) ||
      entry.baseRevision < 0 ||
      entry.baseRevision <= revision ||
      !entry.command ||
      typeof entry.command !== "object" ||
      ![
        "purchase-upgrade",
        "collect-opportunity",
        "set-disclosure",
        "deploy-breakthrough",
        "resolve-event",
        "turn-hostile",
        "set-speed",
        "set-paused",
      ].includes(entry.command.type) ||
      JSON.stringify(entry.command).length > 1024
    )
      throw new Error("Invalid replay archive entry ordering");
    sequence = entry.sequence;
    tick = entry.tick;
    revision = entry.baseRevision;
  }
  if (journal.entries.length && sequence !== journal.totalAccepted)
    throw new Error("Replay window does not end at its accepted sequence");
}
function sameReplayIdentity(a: ReplayJournal | ReplayArchiveIdentity, b: ReplayJournal): boolean { return a.version === b.version && a.rulesVersion === b.rulesVersion && a.numericVersion === b.numericVersion && a.newsVersion === b.newsVersion && a.seed === b.seed && a.startRegion === b.startRegion; }
function journalFromEntries(identity: ReplayJournal | ReplayArchiveIdentity, entries: ReplayEntry[], totalAccepted: number): ReplayJournal {
  const selected = entries.filter(entry => entry.sequence <= totalAccepted).sort((a, b) => a.sequence - b.sequence);
  const complete = selected.length === totalAccepted && selected.every((entry, index) => entry.sequence === index + 1);
  const nonReproducible = "nonReproducible" in identity ? identity.nonReproducible : "truncated" in identity && identity.truncated && identity.firstRetainedSequence === 1;
  return { version: identity.version, rulesVersion: identity.rulesVersion, numericVersion: identity.numericVersion, ...(identity.newsVersion ? { newsVersion: identity.newsVersion } : {}), seed: identity.seed, startRegion: identity.startRegion, entries: structuredClone(selected), totalAccepted, firstRetainedSequence: selected[0]?.sequence ?? 1, truncated: !complete || Boolean(nonReproducible) };
}

async function withFallbackOwnershipLock<T>(campaignId: string, operation: () => T | Promise<T>): Promise<T> {
  if (typeof window !== "undefined" && typeof navigator !== "undefined" && navigator.locks) return navigator.locks.request(`tcp:ownership:${campaignId}`, operation);
  const previous = fallbackOwnershipQueues.get(campaignId) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(operation);
  fallbackOwnershipQueues.set(campaignId, next);
  try { return await next; } finally { if (fallbackOwnershipQueues.get(campaignId) === next) fallbackOwnershipQueues.delete(campaignId); }
}

function hasIndexedDb(): boolean {
  return typeof indexedDB !== "undefined";
}

function usableLocalStorage(): Storage | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const key = "tcp:storage-probe";
    localStorage.setItem(key, "1");
    localStorage.removeItem(key);
    return localStorage;
  } catch {
    return null;
  }
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
  });
}

async function openDatabase(): Promise<IDBDatabase> {
  if (!hasIndexedDb()) throw new Error("IndexedDB is unavailable");
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      const store = database.createObjectStore(STORE_NAME, { keyPath: "id" });
      store.createIndex("campaignId", "campaignId", { unique: false });
    }
  };
  return requestResult(request);
}

function slotKey(campaignId: string, slotId: string): string {
  if (!campaignId || !slotId) throw new Error("Campaign and slot IDs are required");
  if (campaignId.startsWith("\u0000")) throw new Error("Campaign ID uses a reserved storage namespace");
  return `${campaignId}:${slotId}`;
}

function summarize(save: StoredSave): SaveSummary {
  return {
    slotId: save.slotId,
    campaignId: save.campaignId,
    label: save.label,
    kind: save.kind,
    savedAt: save.savedAt,
    revision: save.revision,
    tick: save.tick,
  };
}

/** Small deterministic corruption check; intentionally not an anti-cheat MAC. */
export function saveChecksum(input: Omit<CampaignSaveEnvelope, "checksum">): string {
  const text = JSON.stringify(input);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function createSaveEnvelope(
  slotId: string,
  metadata: CampaignSaveMetadata,
  checkpoint: CheckpointPayload,
): CampaignSaveEnvelope {
  const unsigned: Omit<CampaignSaveEnvelope, "checksum"> = {
    formatVersion: SAVE_FORMAT_VERSION,
    slotId,
    campaignId: metadata.campaignId, label: metadata.label, kind: metadata.kind,
    engineVersion: metadata.engineVersion, rulesVersion: metadata.rulesVersion,
    contentId: metadata.contentId, contentDigest: metadata.contentDigest,
    ...(metadata.replayLineageId === undefined ? {} : { replayLineageId: metadata.replayLineageId }),
    savedAt: checkpoint.createdAt,
    revision: checkpoint.revision,
    tick: checkpoint.tick,
    lastAppliedSequence: checkpoint.lastAppliedSequence,
    serializedCampaign: checkpoint.serializedCampaign,
  };
  return { ...unsigned, checksum: saveChecksum(unsigned) };
}

export function validateSaveEnvelope(value: unknown): CampaignSaveEnvelope {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Save data must be an object");
  }
  const save = value as Partial<CampaignSaveEnvelope>;
  const strings: (keyof CampaignSaveEnvelope)[] = [
    "slotId",
    "campaignId",
    "label",
    "engineVersion",
    "rulesVersion",
    "contentId",
    "contentDigest",
    "savedAt",
    "serializedCampaign",
    "checksum",
  ];
  if (save.formatVersion !== SAVE_FORMAT_VERSION) {
    throw new Error(`Unsupported save format ${String(save.formatVersion)}`);
  }
  for (const key of strings) {
    if (typeof save[key] !== "string") throw new Error(`Invalid save field: ${key}`);
  }
  if (save.kind !== "autosave" && save.kind !== "manual") throw new Error("Invalid save kind");
  if (save.replayLineageId !== undefined && (typeof save.replayLineageId !== "string" || save.replayLineageId.length > 128)) throw new Error("Invalid replay lineage ID");
  for (const key of ["revision", "tick", "lastAppliedSequence"] as const) {
    const number = save[key];
    if (!Number.isSafeInteger(number) || number! < 0) throw new Error(`Invalid save field: ${key}`);
  }
  // Storage records add indexing/fencing fields. Only the portable envelope
  // participates in its checksum, in the same field order as creation.
  const unsigned = {
    formatVersion: save.formatVersion, slotId: save.slotId,
    campaignId: save.campaignId, label: save.label, kind: save.kind,
    engineVersion: save.engineVersion, rulesVersion: save.rulesVersion,
    contentId: save.contentId, contentDigest: save.contentDigest,
    ...(save.replayLineageId === undefined ? {} : { replayLineageId: save.replayLineageId }),
    savedAt: save.savedAt, revision: save.revision, tick: save.tick,
    lastAppliedSequence: save.lastAppliedSequence, serializedCampaign: save.serializedCampaign,
  } as Omit<CampaignSaveEnvelope, "checksum">;
  const checksum = save.checksum!;
  if (saveChecksum(unsigned) !== checksum) throw new Error("Save checksum does not match its contents");
  return { ...unsigned, checksum };
}

export function exportSave(save: CampaignSaveEnvelope, space = 2): string {
  return JSON.stringify(validateSaveEnvelope(save), null, space);
}

export function importSave(text: string, maxBytes = DEFAULT_SAVE_SIZE_LIMIT): CampaignSaveEnvelope {
  if (new TextEncoder().encode(text).byteLength > maxBytes) {
    throw new Error(`Save exceeds the ${maxBytes}-byte import limit`);
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error("Save is not valid JSON");
  }
  return validateSaveEnvelope(value);
}

/** IndexedDB-first slot storage with localStorage and in-memory degradation. */
export class CampaignStorage {
  private databasePromise?: Promise<IDBDatabase>;

  private database(): Promise<IDBDatabase> {
    return (this.databasePromise ??= openDatabase());
  }

  /** Append-once disk pages; routine checkpoint restore never reads this archive. */
  async appendReplayWindow(campaignId: string, journal: ReplayJournal, guard: SaveWriteGuard = {}, lineageId = campaignId): Promise<void> {
    validateReplayWindow(journal);
    if (!lineageId || lineageId.length > 128) throw new Error("Invalid replay lineage ID");
    if (hasIndexedDb()) {
      const database = await this.database();
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const done = transactionDone(transaction);
      const store = transaction.objectStore(STORE_NAME);
      try {
        enforceLeaseRecord(await requestResult(store.get(leaseStorageId(campaignId))) as LeaseRecord | undefined, guard);
        const old = await requestResult(store.get(replayId(lineageId, "identity"))) as ReplayArchiveIdentity | undefined;
        if (old && (!sameReplayIdentity(old, journal) || old.campaignId !== campaignId)) throw new SaveConflictError("Replay lineage belongs to another campaign identity");
        const pages = new Map<number, ReplayPage>();
        for (const entry of journal.entries) {
          const index = Math.floor((entry.sequence - 1) / REPLAY_PAGE_SIZE);
          let page = pages.get(index);
          if (!page) { page = await requestResult(store.get(replayId(lineageId, index))) as ReplayPage | undefined ?? { id: replayId(lineageId, index), campaignId, recordType: "replay-page", entries: [] }; pages.set(index, page); }
          const previous = page.entries.find(item => item.sequence === entry.sequence);
          if (previous && entryIdentity(previous) !== entryIdentity(entry)) throw new SaveConflictError("Replay history branched. Save this branch under a new replay lineage.");
          if (!previous) page.entries.push(structuredClone(entry));
        }
        for (const page of pages.values()) { page.entries.sort((a, b) => a.sequence - b.sequence); store.put(page); }
        store.put({ id: replayId(lineageId, "identity"), campaignId, recordType: "replay-identity", version: journal.version, rulesVersion: journal.rulesVersion, numericVersion: journal.numericVersion, ...(journal.newsVersion ? { newsVersion: journal.newsVersion } : {}), seed: journal.seed, startRegion: journal.startRegion, totalAccepted: Math.max(old?.totalAccepted ?? 0, journal.totalAccepted), nonReproducible: old?.nonReproducible || (journal.truncated && journal.firstRetainedSequence === 1) } satisfies ReplayArchiveIdentity);
        await done;
        return;
      } catch (error) { try { transaction.abort(); } catch { /* already completed */ } await done.catch(() => undefined); throw error; }
    }
    await withFallbackOwnershipLock(campaignId, () => {
      enforceCurrentLease(campaignId, guard);
      const storage = usableLocalStorage();
      const raw = storage?.getItem(`${REPLAY_PREFIX}${lineageId}`);
      const old = raw ? JSON.parse(raw) as ReplayJournal : replayMemory.get(lineageId);
      if (old && !sameReplayIdentity(old, journal)) throw new SaveConflictError("Replay lineage belongs to another campaign identity");
      const entries = new Map((old?.entries ?? []).map(entry => [entry.sequence, entry]));
      for (const entry of journal.entries) {
        const previous = entries.get(entry.sequence);
        if (previous && entryIdentity(previous) !== entryIdentity(entry)) throw new SaveConflictError("Replay history branched. Save this branch under a new replay lineage.");
        if (!previous) entries.set(entry.sequence, structuredClone(entry));
      }
      if (entries.size > REPLAY_FALLBACK_ENTRIES) throw new Error("Full replay archive requires IndexedDB after 512 commands. Export the campaign to preserve its current state.");
      const identity = old?.truncated && old.firstRetainedSequence === 1 ? old : journal;
      const merged = journalFromEntries(identity, [...entries.values()], Math.max(old?.totalAccepted ?? 0, journal.totalAccepted));
      if (storage) storage.setItem(`${REPLAY_PREFIX}${lineageId}`, JSON.stringify(merged));
      else { if (!replayMemory.has(lineageId) && replayMemory.size >= 8) throw new Error("In-memory replay archive limit reached; durable browser storage is unavailable"); replayMemory.set(lineageId, merged); }
    });
  }

  /** Full-history assembly is explicit and bounded; missing prefixes stay marked. */
  async loadReplay(lineageId: string, lastSequence: number): Promise<ReplayJournal | null> {
    if (!Number.isSafeInteger(lastSequence) || lastSequence < 0 || lastSequence > MAX_REPLAY_EXPORT_ENTRIES) throw new Error(`Replay export supports at most ${MAX_REPLAY_EXPORT_ENTRIES} commands`);
    let journal: ReplayJournal | null = null;
    if (hasIndexedDb()) {
      const database = await this.database();
      const transaction = database.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const identity = await requestResult(store.get(replayId(lineageId, "identity"))) as ReplayArchiveIdentity | undefined;
      if (!identity) return null;
      const entries: ReplayEntry[] = [];
      let bytes = 0;
      for (let index = 0; index < Math.ceil(lastSequence / REPLAY_PAGE_SIZE); index++) {
        const page = await requestResult(store.get(replayId(lineageId, index))) as ReplayPage | undefined;
        if (page) {
          bytes += new TextEncoder().encode(JSON.stringify(page.entries)).byteLength;
          if (bytes > DEFAULT_SAVE_SIZE_LIMIT) throw new Error("Replay archive exceeds the export byte limit");
          entries.push(...page.entries);
        }
      }
      journal = journalFromEntries(identity, entries, lastSequence);
    } else {
      const raw = usableLocalStorage()?.getItem(`${REPLAY_PREFIX}${lineageId}`);
      const stored = raw ? JSON.parse(raw) as ReplayJournal : replayMemory.get(lineageId);
      if (stored) journal = journalFromEntries(stored, stored.entries, lastSequence);
    }
    if (journal && new TextEncoder().encode(JSON.stringify(journal)).byteLength > DEFAULT_SAVE_SIZE_LIMIT) throw new Error("Replay archive exceeds the export byte limit");
    return journal;
  }

  async save(envelope: CampaignSaveEnvelope, guard: SaveWriteGuard = {}): Promise<void> {
    const valid = validateSaveEnvelope(envelope);
    const id = slotKey(valid.campaignId, valid.slotId);
    const record: StoredSave = {
      ...valid,
      id,
      fencingToken: guard.fencingToken ?? 0,
    };
    if (hasIndexedDb()) {
      try {
        const database = await this.database();
        const transaction = database.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const done = transactionDone(transaction);
        const existing = (await requestResult(store.get(id))) as StoredSave | undefined;
        const lease = await requestResult(store.get(leaseStorageId(record.campaignId))) as LeaseRecord | undefined;
        enforceLeaseRecord(lease, guard);
        enforceWriteGuard(existing, record, guard);
        store.put(record);
        await done;
        return;
      } catch (error) {
        // Validation/fencing failures must not be hidden by fallback storage.
        if (error instanceof SaveConflictError) throw error;
        this.databasePromise = undefined;
      }
    }
    await withFallbackOwnershipLock(record.campaignId, () => this.saveFallback(record, guard));
  }

  async load(campaignId: string, slotId: string): Promise<CampaignSaveEnvelope | null> {
    const id = slotKey(campaignId, slotId);
    if (hasIndexedDb()) {
      try {
        const database = await this.database();
        const transaction = database.transaction(STORE_NAME, "readonly");
        const result = (await requestResult(transaction.objectStore(STORE_NAME).get(id))) as
          | StoredSave
          | undefined;
        if (result) return validateSaveEnvelope(result);
      } catch {
        this.databasePromise = undefined;
      }
    }
    return this.loadFallback(id);
  }

  async list(campaignId?: string): Promise<SaveSummary[]> {
    let summaries: SaveSummary[] = [];
    if (hasIndexedDb()) {
      try {
        const database = await this.database();
        const transaction = database.transaction(STORE_NAME, "readonly");
        const store = transaction.objectStore(STORE_NAME);
        summaries = await new Promise<SaveSummary[]>((resolve, reject) => {
          const result: SaveSummary[] = [];
          // Internal ownership/replay keys start with NUL. Exclude that range
          // before values are read, so routine listing never loads replay pages.
          const request = store.openCursor(IDBKeyRange.lowerBound("\u0001"));
          request.onerror = () => reject(request.error ?? new Error("Save listing failed"));
          request.onsuccess = () => {
            const cursor = request.result;
            if (!cursor) return resolve(result);
            const record = cursor.value as StoredSave;
            // Replay pages and ownership rows are discarded immediately. Only
            // lightweight save summaries survive each cursor iteration.
            if (typeof record.serializedCampaign === "string" && (!campaignId || record.campaignId === campaignId)) {
              try { validateSaveEnvelope(record); result.push(summarize(record)); } catch { /* skip a corrupt slot */ }
            }
            cursor.continue();
          };
        });
      } catch {
        this.databasePromise = undefined;
      }
    }
    if (summaries.length === 0) summaries = this.listFallback(campaignId);
    return summaries.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  }

  async remove(campaignId: string, slotId: string): Promise<void> {
    const id = slotKey(campaignId, slotId);
    if (hasIndexedDb()) {
      try {
        const database = await this.database();
        const transaction = database.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).delete(id);
        await transactionDone(transaction);
      } catch {
        this.databasePromise = undefined;
      }
    }
    fallbackMemory.delete(id);
    usableLocalStorage()?.removeItem(`${LOCAL_SAVE_PREFIX}${id}`);
  }

  private saveFallback(record: StoredSave, guard: SaveWriteGuard): void {
    const storage = usableLocalStorage();
    const key = `${LOCAL_SAVE_PREFIX}${record.id}`;
    let existing = fallbackMemory.get(record.id);
    if (storage) {
      const raw = storage.getItem(key);
      if (raw) {
        try {
          existing = JSON.parse(raw) as StoredSave;
        } catch {
          // Replace corrupt fallback data only after the new value validates.
        }
      }
    }
    enforceWriteGuard(existing, record, guard);
    enforceCurrentLease(record.campaignId, guard);
    const serialized = JSON.stringify(record);
    if (storage) {
      try {
        storage.setItem(key, serialized);
        return;
      } catch {
        // Keep the run recoverable in-memory when browser quota/privacy blocks writes.
      }
    }
    fallbackMemory.set(record.id, record);
  }

  private loadFallback(id: string): CampaignSaveEnvelope | null {
    const storage = usableLocalStorage();
    const raw = storage?.getItem(`${LOCAL_SAVE_PREFIX}${id}`);
    if (raw) {
      try {
        return validateSaveEnvelope(JSON.parse(raw));
      } catch {
        return null;
      }
    }
    const record = fallbackMemory.get(id);
    return record ? validateSaveEnvelope(record) : null;
  }

  private listFallback(campaignId?: string): SaveSummary[] {
    const records = new Map<string, SaveSummary>();
    for (const record of fallbackMemory.values()) if (!campaignId || record.campaignId === campaignId) records.set(record.id, summarize(record));
    const storage = usableLocalStorage();
    if (storage) {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (!key?.startsWith(LOCAL_SAVE_PREFIX)) continue;
        try {
          const record = JSON.parse(storage.getItem(key) ?? "null") as StoredSave;
          validateSaveEnvelope(record);
          if (!campaignId || record.campaignId === campaignId) records.set(record.id, summarize(record));
        } catch {
          // Ignore corrupt unrelated slots while keeping valid saves available.
        }
      }
    }
    return [...records.values()];
  }
}

export class SaveConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SaveConflictError";
  }
}

function enforceWriteGuard(
  existing: StoredSave | undefined,
  incoming: StoredSave,
  guard: SaveWriteGuard,
): void {
  if (!existing) return;
  if ((guard.fencingToken ?? 0) < existing.fencingToken) {
    throw new SaveConflictError("This tab no longer owns the campaign save");
  }
  if ((guard.rejectOlderRevision ?? true) && incoming.revision < existing.revision) {
    throw new SaveConflictError("A newer campaign revision is already saved in this slot");
  }
}

function enforceCurrentLease(campaignId: string, guard: SaveWriteGuard): void {
  const key = `${LEASE_PREFIX}${campaignId}`;
  const raw = usableLocalStorage()?.getItem(key);
  let lease = memoryLeases.get(key);
  if (raw) { try { lease = JSON.parse(raw) as LeaseRecord; } catch { throw new SaveConflictError("Campaign ownership data is invalid"); } }
  enforceLeaseRecord(lease, guard);
}
function enforceLeaseRecord(lease: LeaseRecord | undefined, guard: SaveWriteGuard): void {
  if (lease && (lease.fencingToken !== guard.fencingToken || lease.expiresAt <= Date.now())) {
    throw new SaveConflictError("This tab no longer owns the campaign save");
  }
}

export interface AutosaveTarget {
  checkpoint(): Promise<CheckpointPayload>;
}

export interface AutosaveOptions {
  storage: CampaignStorage;
  runtime: AutosaveTarget;
  metadata: Omit<CampaignSaveMetadata, "kind" | "label">;
  intervalMs?: number;
  fencingToken?: () => number | undefined;
  onSaved?: (summary: SaveSummary) => void;
  onError?: (error: Error) => void;
}

/** Serializes saves, rotates three slots, and never lets an old checkpoint win. */
export class AutosaveCoordinator {
  private readonly intervalMs: number;
  private timer?: ReturnType<typeof setInterval>;
  private active = false;
  private nextSlot = 0;
  private pending: Promise<void> = Promise.resolve();

  constructor(private readonly options: AutosaveOptions) {
    this.intervalMs = options.intervalMs ?? 30_000;
  }

  start(): void {
    if (this.timer) return;
    this.active = true;
    this.timer = setInterval(() => void this.save("interval"), this.intervalMs);
  }

  stop(): void {
    this.active = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }

  setActive(active: boolean): void {
    this.active = active;
  }

  save(reason: "interval" | "pause" | "background" | "milestone" | "decision"): Promise<void> {
    if (reason === "interval" && !this.active) return this.pending;
    this.pending = this.pending.then(async () => {
      try {
        const checkpoint = await this.options.runtime.checkpoint();
        const slotId = `autosave-${this.nextSlot}`;
        this.nextSlot = (this.nextSlot + 1) % 3;
        const envelope = createSaveEnvelope(
          slotId,
          { ...this.options.metadata, kind: "autosave", label: `Autosave (${reason})` },
          checkpoint,
        );
        await this.options.storage.save(envelope, {
          fencingToken: this.options.fencingToken?.(),
          rejectOlderRevision: true,
        });
        this.options.onSaved?.(summarize({
          ...envelope,
          id: slotKey(envelope.campaignId, envelope.slotId),
          fencingToken: this.options.fencingToken?.() ?? 0,
        }));
      } catch (value) {
        const error = value instanceof Error ? value : new Error(String(value));
        this.options.onError?.(error);
      }
    });
    return this.pending;
  }
}

interface LeaseRecord {
  ownerId: string;
  fencingToken: number;
  expiresAt: number;
}
function leaseStorageId(campaignId: string): string { return `\u0000ownership:${campaignId}`; }

/** All lease changes and save commits share the same IndexedDB write lock. */
async function changeDurableLease(campaignId: string, change: (current: LeaseRecord | null) => LeaseRecord | null): Promise<LeaseRecord | null> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    let current = await requestResult(store.get(leaseStorageId(campaignId))) as LeaseRecord | undefined;
    if (!current) {
      // Upgrade existing slot databases without ever reusing their old fence.
      const previousToken = await new Promise<number>((resolve, reject) => {
        let highest = 0;
        const request = store.index("campaignId").openCursor(campaignId);
        request.onerror = () => reject(request.error ?? new Error("Legacy ownership scan failed"));
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return resolve(highest);
          highest = Math.max(highest, (cursor.value as StoredSave).fencingToken ?? 0);
          cursor.continue();
        };
      });
      if (previousToken) current = { ownerId: "", fencingToken: previousToken, expiresAt: 0 };
    }
    const next = change(current ?? null);
    if (next) store.put({ ...next, id: leaseStorageId(campaignId), recordType: "ownership", campaignId });
    await done;
    return next;
  } finally { database.close(); }
}

export interface CampaignOwnershipOptions {
  ownerId?: string;
  leaseMs?: number;
  heartbeatMs?: number;
  onOwnershipLost?: () => void;
}

/** Durable acquisition and writes are fenced in one IndexedDB object store. */
export class CampaignOwnershipLease {
  readonly ownerId: string;
  private readonly leaseMs: number;
  private readonly heartbeatMs: number;
  private heartbeat?: ReturnType<typeof setInterval>;
  private token = 0;
  private owned = false;
  private disposed = false;
  private readonly storageHandler = (event: StorageEvent): void => {
    if (event.key !== this.key || !this.owned) return;
    const current = this.read();
    if (!current || current.ownerId !== this.ownerId || current.fencingToken !== this.token) {
      this.loseOwnership();
    }
  };

  constructor(
    readonly campaignId: string,
    private readonly options: CampaignOwnershipOptions = {},
  ) {
    this.ownerId = options.ownerId ??
      (globalThis.crypto?.randomUUID?.() ?? `tab-${Date.now()}-${Math.random()}`);
    this.leaseMs = options.leaseMs ?? 15_000;
    this.heartbeatMs = options.heartbeatMs ?? 5_000;
    if (typeof window !== "undefined") window.addEventListener("storage", this.storageHandler);
  }

  get key(): string {
    return `${LEASE_PREFIX}${this.campaignId}`;
  }

  get fencingToken(): number | undefined {
    return this.owned ? this.token : undefined;
  }

  get isOwner(): boolean {
    const current = this.read();
    return this.owned && current?.ownerId === this.ownerId && current.fencingToken === this.token && current.expiresAt > Date.now();
  }

  async acquire(takeOver = false): Promise<boolean> {
    if (this.disposed) return false;
    const rememberedToken = this.read()?.fencingToken ?? 0;
    const change = (current: LeaseRecord | null): LeaseRecord | null => {
      const now = Date.now();
      if (current && current.expiresAt > now && current.ownerId !== this.ownerId && !takeOver) return current;
      return { ownerId: this.ownerId, fencingToken: Math.max(current?.fencingToken ?? 0, this.token, rememberedToken) + 1, expiresAt: now + this.leaseMs };
    };
    const record = hasIndexedDb() ? await changeDurableLease(this.campaignId, change) : await withFallbackOwnershipLock(this.campaignId, () => { const next = change(this.read()); if (next) this.write(next); return next; });
    if (!record || record.ownerId !== this.ownerId) return false;
    this.token = record.fencingToken;
    if (this.disposed) { await this.release(); return false; }
    this.write(record);
    const verified = this.read();
    this.owned = verified?.ownerId === this.ownerId && verified.fencingToken === this.token;
    if (this.owned) this.startHeartbeat();
    return this.owned;
  }

  async release(): Promise<void> {
    this.stopHeartbeat();
    this.owned = false;
    const releasedToken = this.token;
    const change = (current: LeaseRecord | null) => current?.ownerId === this.ownerId && current.fencingToken === releasedToken ? { ...current, expiresAt: 0 } : current;
    const record = hasIndexedDb() ? await changeDurableLease(this.campaignId, change) : await withFallbackOwnershipLock(this.campaignId, () => { const next = change(this.read()); if (next) this.write(next); return next; });
    if (record) this.write(record);
  }

  dispose(): void {
    this.disposed = true;
    void this.release().catch(() => undefined);
    if (typeof window !== "undefined") window.removeEventListener("storage", this.storageHandler);
  }

  private async renew(): Promise<void> {
    if (!this.owned) return;
    const change = (current: LeaseRecord | null) => current?.ownerId === this.ownerId && current.fencingToken === this.token ? { ...current, expiresAt: Date.now() + this.leaseMs } : current;
    const current = hasIndexedDb() ? await changeDurableLease(this.campaignId, change) : await withFallbackOwnershipLock(this.campaignId, () => { const next = change(this.read()); if (next) this.write(next); return next; });
    if (!this.owned) return;
    if (!current || current.ownerId !== this.ownerId || current.fencingToken !== this.token) {
      this.loseOwnership();
      return;
    }
    this.write(current);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => void this.renew().catch(() => this.loseOwnership()), this.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = undefined;
  }

  private loseOwnership(): void {
    this.stopHeartbeat();
    this.owned = false;
    this.options.onOwnershipLost?.();
  }

  private read(): LeaseRecord | null {
    const storage = usableLocalStorage();
    const raw = storage?.getItem(this.key);
    if (raw) {
      try {
        const value = JSON.parse(raw) as LeaseRecord;
        if (
          typeof value.ownerId === "string" &&
          Number.isSafeInteger(value.fencingToken) &&
          Number.isFinite(value.expiresAt)
        ) return value;
      } catch {
        return null;
      }
    }
    return memoryLeases.get(this.key) ?? null;
  }

  private write(record: LeaseRecord): void {
    // Delayed transaction callbacks must not roll back tab notifications.
    const current = this.read();
    if (current && current.fencingToken > record.fencingToken) return;
    const storage = usableLocalStorage();
    if (storage) {
      try {
        storage.setItem(this.key, JSON.stringify(record));
        return;
      } catch {
        // Same-page ownership still works in memory when storage is blocked.
      }
    }
    memoryLeases.set(this.key, record);
  }

}
